"use client";

import { Calendar, User, ArrowRight, Share2, Tag } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="h-12 w-3/4 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-6 w-1/2 bg-muted/30 animate-pulse rounded-md" />
        <div className="h-96 w-full bg-muted/30 animate-pulse rounded-lg" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-muted/30 animate-pulse rounded-md" />
          <div className="h-4 w-full bg-muted/30 animate-pulse rounded-md" />
          <div className="h-4 w-5/6 bg-muted/30 animate-pulse rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <p className="text-muted-foreground">Failed to load tree care article.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <p className="text-muted-foreground">No article found.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorInitials = post.author?.name
    ? post.author.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "AP";

  const authorAvatar =
    post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || post.author?.avatar?.["24"] || "";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          {post.categories.map((cat, i) => (
            <Link
              key={cat.id}
              to={cat.link || "#"}
              data-index={i}
              className="text-xs uppercase tracking-widest text-primary font-bold hover:text-accent transition-colors"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground text-center leading-tight mb-6"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-muted-foreground mb-10">
        {post.author?.name && (
          <div className="flex items-center gap-2">
            <User className="size-4 text-accent" />
            <span className="font-medium">{post.author.name}</span>
          </div>
        )}
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4 text-accent" />
            <span>{formattedDate}</span>
          </div>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-md shadow-lg border border-border">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-invert max-w-none font-default text-base md:text-lg leading-relaxed text-foreground/90 space-y-6 [&>p]:mb-6 [&>h2]:font-default [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:text-foreground [&>h3]:font-default [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:mt-8 [&>h3]:mb-3 [&>h3]:text-foreground [&>a]:text-accent [&>a]:underline [&>blockquote]:border-l-4 [&>blockquote]:border-accent [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>img]:rounded-md [&>img]:my-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mt-12 pt-8 border-t border-border">
          <Tag className="size-4 text-accent" />
          {post.tags.map((tag, i) => (
            <Link
              key={tag.id}
              to={tag.link || "#"}
              data-index={i}
              className="text-xs px-3 py-1 rounded-md bg-muted text-foreground hover:bg-accent hover:text-accent-foreground transition-colors font-medium"
            >
              #{tag.name}
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12 bg-border" />

      {}
      {post.author && (
        <Card className="bg-card border border-border shadow-md rounded-md">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <Avatar className="size-20 shrink-0 border-2 border-accent">
                {authorAvatar && <AvatarImage src={authorAvatar} alt={post.author.name} />}
                <AvatarFallback className="bg-primary text-primary-foreground font-default font-bold text-lg">
                  {authorInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-center sm:text-left">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 font-bold">
                  Expert Contributor
                </p>
                <h3 className="font-default text-2xl font-bold text-foreground mb-2">
                  {post.author.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  Certified Arborist at Arbor Pro, dedicated to preserving the urban canopy of Fort Worth through professional tree health management and sustainable practices.
                </p>
                {post.author.link && (
                  <Link to={post.author.link}>
                    <Button variant="outline" size="sm" className="border-border hover:bg-accent hover:text-accent-foreground rounded-md">
                      View All Articles
                      <ArrowRight className="size-4" />
                    </Button>
                  </Link>
                )}
              </div>
              <Button variant="ghost" size="icon" className="shrink-0 hover:text-accent" aria-label="Share post">
                <Share2 className="size-5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-80 rounded-md bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <p className="text-center text-muted-foreground">No related tree care tips available.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.slice(0, 3).map((post, i) => {
        const date = post.date
          ? new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        return (
          <Card
            key={post.id}
            data-index={i}
            className="bg-card border border-border shadow-md hover:shadow-lg transition-all overflow-hidden group rounded-md"
          >
            {post.featuredImage && (
              <Link to={post.link || "#"} className="block overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
              </Link>
            )}
            <CardContent className="p-6">
              {post.categories && post.categories.length > 0 && (
                <p className="text-xs uppercase tracking-widest text-accent font-bold mb-3">
                  {post.categories[0].name}
                </p>
              )}
              <Link to={post.link || "#"}>
                <h3
                  className="font-default text-xl font-bold text-foreground mb-3 leading-tight hover:text-accent transition-colors line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: post.title }}
                />
              </Link>
              {post.excerpt && (
                <div
                  className="text-sm text-muted-foreground mb-4 line-clamp-2 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: post.excerpt }}
                />
              )}
              <div className="flex items-center justify-between pt-4 border-t border-border">
                {date && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="size-3 text-accent" />
                    <span>{date}</span>
                  </div>
                )}
                <Link
                  to={post.link || "#"}
                  className="text-xs text-primary font-bold hover:text-accent inline-flex items-center gap-1 transition-colors"
                >
                  Read Article
                  <ArrowRight className="size-3" />
                </Link>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="5220"
      id="single-post-template-section"
      className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8"
    >
      <div className="container mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="max-w-6xl mx-auto mt-20 md:mt-28">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-accent font-bold mb-3">
              Arbor Pro Insights
            </p>
            <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground">
              More Tree Care Resources
            </h2>
            <Separator className="w-24 mx-auto mt-6 bg-accent h-1" />
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}